import {
    Features,
    getFeatureFlagValue,
} from '@experiences/feature-flags';
import type {
    IConsumable,
    IConsumptionDataResponse,
    IEntitlementUsageDetailsResponse,
    IGetUsageDetails,
    ILAUsage,
    ITenantUsageResponse,
    IUsageDetails,
} from '@experiences/interfaces';
import { mapUsageDetailsPerMonth } from '@experiences/util';
import type { CancelTokenSource } from 'axios';

import store from '../../store/index';
import {
    post,
    put,
} from '../utility/Requests';
import { getAvailableConsumableEntitlements } from './usage/GetAvailableConsumableEntitlements';

export const licenseUsageUrl = '/api/usage';

// Onprem keeps the Express proxy because the cluster ingress in this repo does
// not expose `/lease_/` to the browser. Cloud calls LA directly.
const isOnPremEnv = () => Boolean(window.env?.isOnPrem) || window.env?.name === 'k8s';

const buildLeasePath = (path: string) => `/lease_/${path.replace(/^\//, '')}`;

// Returns a fresh object on every call: `getHeaders` mutates the passed `headers`
// (it deletes `extendRequestHeaders`), so a shared instance would lose the flag
// after the first request and drop the default `Content-Type` on subsequent ones.
const directCallProps = () => ({
    stripPortalPath: true as const,
    headers: { extendRequestHeaders: 'true' },
});

const LA_TIMELINE = 'api/account/{accountGlobalId}/stats/timeline';
const LA_ORGANIZATION_TIMELINE = 'api/account/{accountGlobalId}/stats/organization/timeline';

function getUsageTimelinePath(accountGlobalId: string, usageAggregationType: string) {
    const enableRobotUnits = getFeatureFlagValue(Features.EnableRobotUnits.name);
    const tenantPath = LA_TIMELINE.replace('{accountGlobalId}', accountGlobalId);
    const organizationPath = LA_ORGANIZATION_TIMELINE.replace('{accountGlobalId}', accountGlobalId);

    if (!enableRobotUnits) {
        return tenantPath;
    }
    return usageAggregationType === 'ORGANIZATION' ? organizationPath : tenantPath;
}

// getLicenseUsageTimeline keeps its existing global-store lookup since callers
// (RobotUsageAction) don't have direct access to a useSelector hook.
export function getLicenseUsageTimeline(
    clientIds: string[],
    timePeriod: number,
    usageAggregationType: string,
    source?: CancelTokenSource,
) {
    const accountGlobalId = store.getState().profile.accountUserDto.accountGlobalId.toLowerCase();
    const body = {
        accountGlobalId,
        clientIds,
        timePeriod,
        usageAggregationType,
    };

    if (isOnPremEnv()) {
        return post<ILAUsage[]>(`${licenseUsageUrl}/getTimelineForAccount`, {
            body,
            cancelTokenSource: source,
        });
    }

    return post<ILAUsage[]>(buildLeasePath(getUsageTimelinePath(accountGlobalId, usageAggregationType)), {
        ...directCallProps(),
        body,
        cancelTokenSource: source,
    });
}

export async function getUsageDetails(
    {
        entitlements, accountGlobalId,
    }: { url: string; entitlements: string[]; accountGlobalId?: string },
): Promise<IEntitlementUsageDetailsResponse[] | undefined> {
    if (isOnPremEnv()) {
        const usageDetails: IGetUsageDetails = { entitlements };
        return put<IEntitlementUsageDetailsResponse[]>(
            `${licenseUsageUrl}/pools/details`,
            { body: usageDetails },
        );
    }

    const available = getAvailableConsumableEntitlements();
    const entitlementsToRequest = entitlements.filter(e => available.includes(e));
    if (entitlementsToRequest.length === 0 || !accountGlobalId) {
        return undefined;
    }

    const response = await put<IUsageDetails>(
        buildLeasePath(`api/usage/${accountGlobalId}/pools/details`),
        {
            ...directCallProps(),
            body: { entitlements: entitlementsToRequest } as IGetUsageDetails,
        },
    );

    if (!response || !Array.isArray(response.entitlements)) {
        return undefined;
    }

    return mapUsageDetailsPerMonth(response, new Date());
}

export function getConsumptionData({
    startDate, endDate, consumableCode, services, aggregationPeriod, tenantId, accountGlobalId,
}: {
    url: string;
    startDate: number;
    endDate: number;
    consumableCode: string;
    services: string[];
    aggregationPeriod: string;
    tenantId?: string;
    accountGlobalId?: string;
}) {
    const body = {
        startDate,
        endDate,
        consumableCode,
        services,
        aggregationPeriod,
        tenantId,
    };

    if (isOnPremEnv()) {
        return put<IConsumptionDataResponse>(`${licenseUsageUrl}/services`, { body });
    }

    return put<IConsumptionDataResponse>(
        buildLeasePath(`api/usage/${accountGlobalId}/services`),
        {
            ...directCallProps(),
            body,
        },
    );
}

export async function getConsumptionDataByTenant({
    startDate, endDate, consumableCode, services, aggregationPeriod, tenantId, accountGlobalId,
}: {
    url: string;
    startDate: number;
    endDate: number;
    consumableCode: string;
    services: string[];
    aggregationPeriod: string;
    tenantId?: string;
    accountGlobalId?: string;
}) {
    if (!tenantId) {
        throw new Error('getConsumptionDataByTenant requires a tenantId');
    }
    const body = {
        startDate,
        endDate,
        consumableCode,
        services,
        aggregationPeriod,
    };

    if (isOnPremEnv()) {
        return put<IConsumptionDataResponse>(`${licenseUsageUrl}/tenants/${tenantId}/services`, { body });
    }

    return put<IConsumptionDataResponse>(
        buildLeasePath(`api/usage/${accountGlobalId}/tenants/${tenantId}/services`),
        {
            ...directCallProps(),
            body,
        },
    );
}

export function getTenantUsageData({
    startDate, endDate, consumableCode, tenantId, accountGlobalId,
}: {
    url: string;
    startDate: number | undefined;
    endDate: number | undefined;
    consumableCode: string;
    tenantId?: string;
    accountGlobalId?: string;
}): Promise<ITenantUsageResponse> {
    const body = {
        startDate,
        endDate,
        consumableCode,
        tenantId,
    };

    if (isOnPremEnv()) {
        return put<ITenantUsageResponse>(`${licenseUsageUrl}/tenants`, { body });
    }

    return put<ITenantUsageResponse>(
        buildLeasePath(`api/usage/${accountGlobalId}/tenants`),
        {
            ...directCallProps(),
            body,
        },
    );
}

export function getDefaultConsumptionPool(consumable: IConsumable) {
    if (consumable.pools.length === 1) {
        return [ consumable.pools[0].startDate, consumable.pools[0].endDate ];
    }
    const defaultPool = consumable.pools.find(pool => pool.name.toLowerCase().endsWith('default'));
    return defaultPool ? [ defaultPool.startDate, defaultPool.endDate ] : [];
}
